import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import PageLoader from '../components/PageLoader';
import HELP_ARTICLES, { TIER_LABELS, TIER_COLORS } from '../data/helpArticles';
import { parseFrontmatter } from '../utils/parseFrontmatter';

export default function Help() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [sections, setSections] = useState(null);

  useEffect(() => {
    Promise.all(
      HELP_ARTICLES.map(({ file, slug }) =>
        fetch(`/help/${file}`)
          .then(r => r.text())
          .then(raw => ({ slug, ...parseFrontmatter(raw).data }))
      )
    ).then(articles => {
      const bySection = {};
      articles.forEach(a => {
        if (!bySection[a.section]) bySection[a.section] = [];
        bySection[a.section].push(a);
      });
      const grouped = Object.values(bySection).map(list => {
        list.sort((a, b) => Number(a.order) - Number(b.order));
        return { name: list[0].section, minOrder: Math.min(...list.map(a => Number(a.order))), articles: list };
      });
      grouped.sort((a, b) => a.minOrder - b.minOrder);
      setSections(grouped);
    }).catch(() => setSections([]));
  }, []);

  if (!sections) return <PageLoader />;

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '1rem 1rem 2rem' }}>
      <p style={{ fontSize: '0.95rem', color: theme.textSecondary, margin: '0 0 1.25rem' }}>
        Guides for getting the most out of GigRequest — from your first sign-in to running a live show.
      </p>

      {sections.map(section => (
        <div key={section.name} style={{ marginBottom: '1.5rem' }}>
          <h2 style={{
            fontSize: '0.78rem',
            fontWeight: 700,
            color: theme.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            margin: '0 0 0.6rem 0.15rem',
          }}>
            {section.name}
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {section.articles.map(article => (
              <button
                key={article.slug}
                onClick={() => navigate(`/dashboard/help/${article.slug}`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  width: '100%',
                  textAlign: 'left',
                  background: theme.surface,
                  border: `0.5px solid ${theme.border}`,
                  borderRadius: 12,
                  padding: '0.9rem 1rem',
                  cursor: 'pointer',
                  boxShadow: theme.isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.06)',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: theme.text, marginBottom: '0.3rem' }}>
                    {article.title}
                  </div>
                  <span style={{
                    display: 'inline-block',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    padding: '0.12rem 0.5rem',
                    borderRadius: 10,
                    background: `${TIER_COLORS[article.tier] || TIER_COLORS.starter}22`,
                    color: TIER_COLORS[article.tier] || TIER_COLORS.starter,
                  }}>
                    {TIER_LABELS[article.tier] || article.tier}
                  </span>
                </div>
                <ChevronRight size={20} color={theme.textMuted} style={{ flexShrink: 0 }} />
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
